import classnames from "classnames";
import React from "react";

import styled from "styled-components";
import { IconType } from "./IconType";
import {
  ColorDescription,
  getColorByDescription,
  themeColors,
} from "../CareConnectMobile-Theme";

export interface Props extends IconProps {
  /** Type of the icon */
  type: IconType;
  /** Disable line height*/
  noLineHeight?: boolean;
  /** Display a circle around the icon */
  circle?: boolean;
  /** Optional override for margin */
  margin?: string;
}

interface IconProps extends React.HTMLAttributes<HTMLElement> {
  /** Color of icon */
  color?: ColorDescription;
  /** Optional background color for circle, default white */
  backgroundColor?: ColorDescription;
  /** Variant based on typography sizes */
  variant?: "sm" | "p" | "h1" | "h2" | "h3" | "h4" | "huge";
}

/** Icon wrapper for the icon font */
const Icon = ({
  type,
  className,
  noLineHeight,
  circle,
  backgroundColor = "white",
  ...others
}: Props) => {
  const iconClassNames = classnames(
    "if",
    "if-" + type,
    { "if--line": !noLineHeight },
    className
  );

  if (!!circle) {
    return (
      <Circle backgroundColor={backgroundColor} {...others}>
        <StyledIcon {...others} className={iconClassNames} />
      </Circle>
    );
  } else {
    return <StyledIcon {...others} className={iconClassNames} />;
  }
};

const StyledIcon = styled.i<IconProps>`
  font-size: ${getFontSize};
`;

const Circle = styled.div<IconProps>`
  border-radius: 50%
  box-shadow: ${(props) =>
    "0 1px 3px " +
    themeColors(props).border +
    ", 0 0 0 1px " +
    themeColors(props).border +
    ";"};
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${getCircleSize};
  height ${getCircleSize};
  min-width: ${getCircleSize};
  min-height ${getCircleSize};
  background-color: ${(props) =>
    getColorByDescription(props, props.backgroundColor)};
`;

function getCircleSize({ variant }: IconProps) {
  switch (variant) {
    case "huge":
      return "7.6rem";
    case "h1":
      return "3.8rem";
    case "h2":
      return "3.4rem";
    case "h3":
      return "3.2rem";
    case "h4":
      return "3.1rem";
    case "sm":
      return "2.5rem";
    default:
      return "2.8rem";
  }
}

function getFontSize({ variant }: IconProps) {
  switch (variant) {
    case "huge":
      return "5.2rem";
    case "h1":
      return "2.6rem";
    case "h2":
      return "2.2rem";
    case "h3":
      return "2.0rem";
    case "h4":
      return "1.8rem";
    case "sm":
      return "1.3rem";
    default:
      return "inherit";
  }
}

export default styled(Icon)<Props>`
  ${(props) => props.margin && `margin:` + props.margin};
  color: ${(props) => getColorByDescription(props, props.color)};
`;
